<script lang="ts" setup>
import { LocalStorageKeys } from '@/constants/enums/LocalStorageEnum';
import type { StorageSexValue, StorageUserStore } from '@/models/interface/storageValue';
import { useAppStore } from '@/stores/app';
import { useUserStore } from '@/stores/user';
import { UtilCommon } from '@/utils/utilCommon';
import 'swiper/css';
import { Autoplay, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { computed, onMounted, onUnmounted, ref, type Ref } from 'vue';

const modules = [Autoplay, Pagination]; // swiper 模塊

type Area = 'listening' | 'speaking' | 'reading' | 'writing';
type ElementDataType = { bgSrc: string; modelSrc: string; textSrc: string; loaded: boolean };

const formChoose = defineModel('formChoose');
const { userData } = useUserStore();
const appStore = useAppStore();

const textGifEle = ref<HTMLImageElement | null>(null);
const listeningEle = ref<HTMLElement | null>(null);
const readingEle = ref<HTMLElement | null>(null);
const speakingEle = ref<HTMLElement | null>(null);
const writingEle = ref<HTMLElement | null>(null);

let flag = false;
let eachColumnTotalLoadedCount = 2; // (桌機版)每個區塊必須讀取完成圖片數量
const haveToClickCount = 4;
const isPlayVideo: Ref<boolean> = ref(false);
const isOpenAnimate: Ref<boolean> = ref(false);
const currentIndex: Ref<number> = ref(0); // 當前圖片index(桌機版)
const swiperOpen = ref(false);
const sex = ref(0); //預設女生

const gifDataList: { [key: string]: string } = {
  listeningEle: '/images/login/Text_Listening.gif',
  readingEle: '/images/login/Text_Reading.gif',
  speakingEle: '/images/login/Text_Speaking.gif',
  writingEle: '/images/login/Text_Writing.gif'
};

// 聽力(桌機版)
const listeningDataM: ElementDataType = {
  bgSrc: '/images/login/ListeningBG_male.jpg',
  modelSrc: '/images/login/ListeningMD_male.png',
  textSrc: '/images/login/Text_Listening.gif',
  loaded: false
};
const listeningDataF: ElementDataType = {
  bgSrc: '/images/login/ListeningBG.jpg',
  modelSrc: '/images/login/ListeningMD.png',
  textSrc: '/images/login/Text_Listening.gif',
  loaded: false
};
// 閱讀(桌機版)
const readingDataM: ElementDataType = {
  bgSrc: '/images/login/ReadingBG_male.jpg',
  modelSrc: '/images/login/ReadingMD_male.png',
  textSrc: '/images/login/Text_Reading.gif',
  loaded: false
};
const readingDataF: ElementDataType = {
  bgSrc: '/images/login/ReadingBG.jpg',
  modelSrc: '/images/login/ReadingMD.png',
  textSrc: '/images/login/Text_Reading.gif',
  loaded: false
};
// 口說(桌機版)
const speakingDataM: ElementDataType = {
  bgSrc: '/images/login/SpeakingBG_male.jpg',
  modelSrc: '/images/login/SpeakingMD_male.png',
  textSrc: '/images/login/Text_Speaking.gif',
  loaded: false
};
const speakingDataF: ElementDataType = {
  bgSrc: '/images/login/SpeakingBG.jpg',
  modelSrc: '/images/login/SpeakingMD.png',
  textSrc: '/images/login/Text_Speaking.gif',
  loaded: false
};
// 寫作(桌機版)
const writingDataM: ElementDataType = {
  bgSrc: '/images/login/WritingBG_male.jpg',
  modelSrc: '/images/login/WritingMD_male.png',
  textSrc: '/images/login/Text_Writing.gif',
  loaded: false
};
const writingDataF: ElementDataType = {
  bgSrc: '/images/login/WritingBG.jpg',
  modelSrc: '/images/login/WritingMD.png',
  textSrc: '/images/login/Text_Writing.gif',
  loaded: false
};

const listeningData = computed(() => (sex.value ? listeningDataM : listeningDataF));
const readingData = computed(() => (sex.value ? readingDataM : readingDataF));
const speakingData = computed(() => (sex.value ? speakingDataM : speakingDataF));
const writingData = computed(() => (sex.value ? writingDataM : writingDataF));

// 每個區塊讀取圖片數量(桌機版)
const loadedCount = ref({
  listening: 0,
  speaking: 0,
  reading: 0,
  writing: 0
});

// 圖片是否全部載入(桌機版)
const isDesktopImgAllLoaded = computed(
  () =>
    listeningData.value.loaded &&
    readingData.value.loaded &&
    speakingData.value.loaded &&
    writingData.value.loaded
);

const handleImgLoad = (data: ElementDataType, area: Area, type: string) => {
  console.log('[handleImgLoad]');
  console.log(`${area} Img loaded: ${type} `);
  loadedCount.value[area]++;
  if (loadedCount.value[area] === eachColumnTotalLoadedCount) {
    data.loaded = true;
  }
};

/** 延遲動畫確保前一個動畫結束 */
const delayAnimation = (element: HTMLElement, delayTime: number) => {
  return new Promise((resolve) => {
    window.setTimeout(() => {
      animate(element);
      resolve(true);
    }, delayTime);
  });
};

const animationStart = async () => {
  console.log('[animationStart]');
  // TODO
  // 1. Landing Text Animation Fade In

  await delayAnimation(listeningEle.value as HTMLElement, 5000);
  await delayAnimation(readingEle.value as HTMLElement, 6500);
  await delayAnimation(speakingEle.value as HTMLElement, 6500);
  await delayAnimation(writingEle.value as HTMLElement, 6500);

  isPlayVideo.value = false;
  console.log('[animationStart] ended');
};

const animate = (target: HTMLElement) => {
  console.log('[animate]');

  const columns = document.querySelectorAll('.animation .column');

  columns.forEach((col) => {
    if (col !== target) {
      (col as HTMLElement).style.flexBasis = '0';
    } else {
      col.classList.add('open');

      window.setTimeout(() => {
        textGifEle.value?.classList.add('show');
      }, 500);
    }
  });
  if (target?.style) {
    target.style.flexBasis = '100%';
  }
  if (!textGifEle.value) {
    return;
  }
  switch (target) {
    case listeningEle.value:
      (textGifEle.value as HTMLImageElement).src = gifDataList.listeningEle;
      break;
    case readingEle.value:
      (textGifEle.value as HTMLImageElement).src = gifDataList.readingEle;
      break;
    case speakingEle.value:
      (textGifEle.value as HTMLImageElement).src = gifDataList.speakingEle;
      break;
    case writingEle.value:
      (textGifEle.value as HTMLImageElement).src = gifDataList.writingEle;
      break;
  }

  if (isPlayVideo.value) {
    window.setTimeout(() => {
      columns.forEach((col) => {
        (col as HTMLElement).style.flexBasis = '25%';
        col.classList.remove('open');
        textGifEle.value?.classList.remove('show');
      });
    }, 5000);
  }
};

let currentClickCount = 0;
const openHandler = (index: number) => {
  if (isPlayVideo.value) return;

  currentIndex.value = index;
  isOpenAnimate.value = true;

  if (index === 0) {
    animate(listeningEle.value as HTMLElement);
  } else if (index === 1) {
    animate(readingEle.value as HTMLElement);
  } else if (index === 2) {
    animate(speakingEle.value as HTMLElement);
  } else if (index === 3) {
    animate(writingEle.value as HTMLElement);
  }
};

const closeHandler = () => {
  currentIndex.value = 0;
  isOpenAnimate.value = false;

  const columns = document.querySelectorAll('.animation .column');

  columns.forEach((col) => {
    (col as HTMLElement).style.flexBasis = '25%';
    (col as HTMLElement).classList.remove('open');
    textGifEle.value?.classList.remove('show');
  });
};

const openNextHandler = (index: number) => {
  if (flag) return;

  textGifEle.value?.classList.remove('show');

  currentIndex.value = index;
  currentClickCount++;

  if (currentClickCount < haveToClickCount) {
    // 點擊次數少於4次 => 還有照片沒點擊

    currentIndex.value++;
    if (currentIndex.value === 4) {
      currentIndex.value = 0;
    }

    openHandler(currentIndex.value);
  } else {
    // 全部照片都點擊過了
    closeHandler();
    currentClickCount = 0;
  }

  // 設定 flag 為 true，防止在一定時間內連續觸發
  flag = true;
  window.setTimeout(() => {
    flag = false; // 在延遲後將 flag 設為 false，允許下一次觸發
  }, 5000);
};

const handleResize = () => {
  swiperOpen.value = window.innerWidth <= 1200;
};

const initialLoading = async () => {
  if (isDesktopImgAllLoaded) {
    // 全部桌機圖片載入完成
    console.log('All Desktop Img are loaded!');
    isPlayVideo.value = true;
    // 開始播放動畫
    await animationStart();
    return;
  }
  initialLoading();
};

onMounted(() => {
  if (appStore.isMobile) {
    swiperOpen.value = appStore.isMobile;
  }

  //抓男生版還是女生版
  const storageUser = UtilCommon.getLocalStorage<StorageUserStore>(LocalStorageKeys.UserStore);
  const storage = UtilCommon.getLocalStorage<StorageSexValue[]>(LocalStorageKeys.Gender);

  const storageValue = storage?.find(
    (item) => item.userAccount === (storageUser?.userAccount ?? '')
  );

  if (storageValue) {
    // sex.value = storageValue.value; 關閉男版
  }

  window.addEventListener('resize', handleResize);
  initialLoading();
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<template>
  <div class="default-animate">
    <div v-if="!swiperOpen" class="animation">
      <div ref="listeningEle" class="column" @click="openHandler(0)">
        <img
          class="bg"
          :src="listeningData.bgSrc"
          alt="backgroundOfListening"
          @load="handleImgLoad(listeningData, 'listening', 'bg')"
        />
        <img
          class="model"
          :src="listeningData.modelSrc"
          alt="modelOfListening"
          @load="handleImgLoad(listeningData, 'listening', 'model')"
        />
      </div>
      <div ref="readingEle" class="column" @click="openHandler(1)">
        <img
          class="bg"
          :src="readingData.bgSrc"
          alt="backgroundOfReading"
          @load="handleImgLoad(readingData, 'reading', 'bg')"
        />
        <img
          class="model"
          :src="readingData.modelSrc"
          alt="modelOfReading"
          @load="handleImgLoad(readingData, 'reading', 'model')"
        />
      </div>
      <div ref="speakingEle" class="column" @click="openHandler(2)">
        <img
          class="bg"
          :src="speakingData.bgSrc"
          alt="backgroundOfSpeaking"
          @load="handleImgLoad(speakingData, 'speaking', 'bg')"
        />
        <img
          class="model"
          :src="speakingData.modelSrc"
          alt="modelOfSpeaking"
          @load="handleImgLoad(speakingData, 'speaking', 'model')"
        />
      </div>
      <div ref="writingEle" class="column" @click="openHandler(3)">
        <img
          class="bg"
          :src="writingData.bgSrc"
          alt="backgroundOfWriting"
          @load="handleImgLoad(writingData, 'writing', 'bg')"
        />
        <img
          class="model"
          :src="writingData.modelSrc"
          alt="modelOfWriting"
          @load="handleImgLoad(writingData, 'writing', 'model')"
        />
      </div>

      <div class="text-wrapper">
        <p class="text-landing">學習 隨手可得</p>
      </div>

      <div
        v-if="!isPlayVideo && isOpenAnimate"
        class="goNextAnimate"
        @click="openNextHandler(currentIndex)"
      >
        Next
      </div>

      <img ref="textGifEle" class="text-gif" src="" />
    </div>

    <swiper
      v-else
      class="fullPage"
      :spaceBetween="30"
      :autoplay="{
        delay: 3500,
        disableOnInteraction: false
      }"
      :pagination="{
        clickable: true
      }"
      :modules="modules"
    >
      <swiper-slide>
        <div class="swiper-image-wrapper">
          <img
            :src="`/images/login/${sex ? 'listening_mobile_male.png' : 'listening_mobile.jpg'}`"
            alt="backgroundOfListening"
          />
        </div>
      </swiper-slide>

      <swiper-slide>
        <div class="swiper-image-wrapper">
          <img
            :src="`/images/login/${sex ? 'reading_mobile_male.png' : 'reading_mobile.png'}`"
            alt="backgroundOfReading"
          />
        </div>
      </swiper-slide>

      <swiper-slide>
        <div class="swiper-image-wrapper">
          <img
            :src="`/images/login/${sex ? 'speaking_mobile_male.png' : 'speaking_mobile.jpg'}`"
            alt="backgroundOfSpeaking"
          />
        </div>
      </swiper-slide>

      <swiper-slide>
        <div class="swiper-image-wrapper">
          <img
            :src="`/images/login/${sex ? 'writing_mobile_male.png' : 'writing_mobile.jpg'}`"
            alt="backgroundOfWriting"
          />
        </div>
      </swiper-slide>
    </swiper>
  </div>
</template>

<style lang="scss" scoped>
$column_open_duration: 1.5s;

.default-animate {
  @include wh;
}

.animation {
  position: relative;
  display: flex;
  @include wh;

  &:after {
    content: '';
    @include absolute($T: 0, $L: 0);
    @include wh;
    background: linear-gradient(to bottom, rgba(25, 25, 25, 0) 80%, #191919 100%);
    pointer-events: none;
  }
}

.column {
  position: relative;
  width: 100%;
  flex: 1 1 25%;
  transition: flex-basis 1.5s ease;
  overflow: hidden;
  height: 100vh;

  // &:hover {
  //     img {
  //         filter: grayscale(00%);
  //     }
  // }

  img {
    position: absolute;
    display: block;
    height: 100%;
    pointer-events: none;
    // filter: grayscale(100%);

    transition:
      all $column_open_duration ease,
      filter 0.3s ease-in;

    &.bg {
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100%;
    }

    &.model {
      // left: 50%;
    }
  }

  /** Listening */
  &:nth-child(1) {
    img.model {
      top: 100px;
      left: 5%;
      transform: scale(1.2);

      @media (min-width: calc(1700px - 0.02px)) and (max-width: 1800px) {
        left: 0%;
      }

      @media (min-width: calc(1600px - 0.02px)) and (max-width: 1700px) {
        left: 0%;
      }

      @media (min-width: calc(1500px - 0.02px)) and (max-width: 1600px) {
        left: -2%;
      }

      @media (min-width: calc(1400px - 0.02px)) and (max-width: 1500px) {
        left: -5%;
      }

      @media (min-width: calc(1300px - 0.02px)) and (max-width: 1400px) {
        left: -10%;
      }

      @media (min-width: 1200px) and (max-width: 1300px) {
        left: -15%;
      }
    }

    &.open {
      // img {
      //     filter: grayscale(0%);
      // }

      img.model {
        top: 200px;

        transform: scale(1.5);

        @media (min-width: calc(1500px - 0.02px)) and (max-width: 1800px) {
          left: 10%;
        }

        @media (min-width: calc(1300px - 0.02px)) and (max-width: 1500px) {
          left: 10%;
        }

        @media (min-width: 1200px) and (max-width: 1300px) {
          left: 0%;
        }
      }
    }
  }

  /** Reading */
  &:nth-child(2) {
    img.model {
      top: 0;
      right: -50%;
      transform: scale(1.3);

      @media (min-width: calc(1700px - 0.02px)) and (max-width: 1800px) {
        right: -60%;
      }

      @media (min-width: calc(1600px - 0.02px)) and (max-width: 1700px) {
        right: -65%;
      }

      @media (min-width: calc(1500px - 0.02px)) and (max-width: 1600px) {
        right: -75%;
      }

      @media (min-width: calc(1400px - 0.02px)) and (max-width: 1500px) {
        right: -85%;
      }

      @media (min-width: calc(1350px - 0.02px)) and (max-width: 1400px) {
        right: -90%;
      }

      @media (min-width: calc(1300px - 0.02px)) and (max-width: 1350px) {
        right: -100%;
      }

      @media (min-width: 1200px) and (max-width: 1300px) {
        right: -105%;
      }
    }

    &.open {
      // img {
      //     filter: grayscale(0%);
      // }

      img.model {
        top: 200px;
        right: 10%;
        transform: scale(1.8);
      }
    }
  }

  /** Listening */
  &:nth-child(3) {
    img.model {
      top: 30px;
      left: -20%;
      transform: scale(1.2);

      @media (min-width: calc(1700px - 0.02px)) and (max-width: 1800px) {
        left: -25%;
      }

      @media (min-width: calc(1600px - 0.02px)) and (max-width: 1700px) {
        left: -30%;
      }

      @media (min-width: calc(1500px - 0.02px)) and (max-width: 1600px) {
        left: -40%;
      }

      @media (min-width: calc(1400px - 0.02px)) and (max-width: 1500px) {
        left: -45%;
      }

      @media (min-width: calc(1300px - 0.02px)) and (max-width: 1400px) {
        left: -55%;
      }

      @media (min-width: calc(1200px - 0.02px)) and (max-width: 1300px) {
        left: -60%;
      }
    }

    &.open {
      // img {
      //     filter: grayscale(0%);
      // }

      img.model {
        top: 150px;
        left: 10%;

        transform: scale(1.5);
      }
    }
  }

  /** Writing */
  &:nth-child(4) {
    img.model {
      top: 120px;
      right: -25%;
      transform: scale(1.2);

      @media (min-width: calc(1700px - 0.02px)) and (max-width: 1800px) {
        right: -30%;
      }

      @media (min-width: calc(1600px - 0.02px)) and (max-width: 1700px) {
        right: -35%;
      }

      @media (min-width: calc(1500px - 0.02px)) and (max-width: 1600px) {
        right: -40%;
      }

      @media (min-width: calc(1400px - 0.02px)) and (max-width: 1500px) {
        right: -50%;
      }

      @media (min-width: calc(1300px - 0.02px)) and (max-width: 1400px) {
        right: -55%;
      }

      @media (min-width: 1200px) and (max-width: 1300px) {
        right: -60%;
      }
    }

    &.open {
      // img {
      //     filter: grayscale(0%);
      // }

      img.model {
        top: 230px;
        right: 10%;
        transform: scale(1.4);
      }
    }
  }
}

.text-wrapper {
  position: absolute;
  @include wh;
  pointer-events: none;

  transition: opacity 1.5s ease;

  .text {
    &-landing {
      @include absolute($T: 50%, $L: 50%);
      @apply text-32 line-150 color-white;
      letter-spacing: 24px;
      transform: translate(-50%, 0);

      opacity: 0;
    }
  }
}

.text-gif {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 80%;

  opacity: 0;
  pointer-events: none;
  mix-blend-mode: screen;

  &.show {
    // opacity: 1;
    animation: fadeInAnimate 0.5s ease 1 forwards;
    animation-play-state: running;
    animation-delay: 300ms;
  }
}

@keyframes fadeInAnimate {
  0% {
    transform: translate(-50%, -50%) scale(0);
    opacity: 0;
  }

  100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
}

@keyframes fadeOutAnimate {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }

  100% {
    transform: translate(-50%, -50%) scale(0);
    opacity: 0;
  }
}

.goNextAnimate {
  position: absolute;
  bottom: 32px;
  right: 5%;
  font-size: 32px;
  line-height: 150%;
  color: $txt-color-white-40;
  letter-spacing: 4px;
  width: 260px;
  transition: all 0.5s ease;
  cursor: pointer;
  z-index: 1;

  &:before {
    position: absolute;
    bottom: 22px;
    right: -8px;
    content: '';
    height: 1px;
    width: 60px;
    background: $txt-color-white-40;
    transform: rotate(45deg);
  }

  &:after {
    position: absolute;
    bottom: 0;
    left: 0;
    content: '';
    height: 1px;
    width: 100%;
    background: $txt-color-white-40;
  }

  &:hover {
    color: $txt-color-white-80;

    &:before {
      background: $txt-color-white-60;
    }

    &:after {
      background: $txt-color-white-60;
    }
  }
}

.swiper-image-wrapper {
  position: relative;
  width: 100%;
  height: 100dvh;
  background: #191919;

  img {
    width: 100%;
    height: 100%;
    @include absolute;
    left: 50%;
    transform: translateX(-50%);
    object-fit: cover;
  }
}

.join-btn--mobile {
  @include absolute;
  z-index: 2;
  left: 50%;
  bottom: 180px;
  transform: translateX(-50%);
  width: 50%;
}

.swiper {
  .swiper-pagination {
    bottom: 180px !important;
  }
}
</style>
